import { Box, Flex, useToken } from "@chakra-ui/react";
import { destination } from "@turf/turf";
import { useCallback, useEffect, useState } from "react";
import { useMap } from "react-map-gl/maplibre";

import { SIZES } from "@nens/ranalib";

// Distance candidates (meters)
const DISTANCE_CANDIDATES = [
  1,
  2,
  5,
  10,
  20,
  50,
  100,
  200,
  500,
  1000,
  2000,
  5000,
  10000,
  20000,
  50000,
  100000,
  200000,
  500000,
  1000000,
  2000000,
  5000000,
  10000000,
  20000000,
  40000000, // up to Earth's circumference
];
const MIN_PX = 70;
const MAX_PX = 160;

export type RanaMapScaleProps = {
  right: keyof typeof SIZES | string;
};

export const RanaMapScale = ({ right }: RanaMapScaleProps) => {
  const map = useMap().current;
  const [animatingWidth, setAnimatingWidth] = useState(false);
  const [scale, setScale] = useState<{ px: number; meters: number } | null>(null);

  const computeScale = useCallback(() => {
    if (!map?.getCenter || !map.project) return;
    const center = map.getCenter();
    if (!center) return;

    // Helper to get pixel width for a given distance (meters) eastwards
    const measureDistance = (meters: number) => {
      const from: [number, number] = [center.lng, center.lat];
      const km = meters / 1000;
      // Get coordinate to the east and slice to remove altitude effects [lng, lat, alt] -> [lng, lat]
      const to = destination(from, km, 90, { units: "kilometers" }).geometry.coordinates.slice(
        0,
        2
      ) as [number, number];
      const p1 = map.project(from);
      const p2 = map.project(to);
      return Math.abs(p2.x - p1.x);
    };

    // Choose candidate that yields width in nice visual range
    let bestChoice: { meters: number; px: number } | null = null;

    for (const meters of DISTANCE_CANDIDATES) {
      const px = measureDistance(meters);

      if (px >= MIN_PX && px <= MAX_PX) {
        bestChoice = { meters, px };
        break;
      }
      if (!bestChoice || (px > bestChoice.px && bestChoice.px < MIN_PX)) {
        bestChoice = { meters, px };
      }
    }

    if (bestChoice && Number.isFinite(bestChoice.px) && bestChoice.px > 0) {
      setScale((prev) => {
        if (prev && prev.meters !== bestChoice.meters) {
          setAnimatingWidth(true);
        }
        return bestChoice;
      });
    }
  }, [map, setScale]);

  useEffect(() => {
    if (!map) return;
    computeScale();
    map.on("move", computeScale);
    map.on("zoom", computeScale);
    return () => {
      map.off("move", computeScale);
      map.off("zoom", computeScale);
    };
  }, [map, computeScale]);

  const [baseBorderWidth] = useToken("borderWidths", ["base"]);

  if (!scale) return null;

  const scaleText =
    scale.meters >= 1000
      ? `${(scale.meters / 1000).toFixed(scale.meters % 1000 === 0 ? 0 : 1)} km`
      : `${scale.meters} m`;

  return (
    <Flex
      position="absolute"
      right={right}
      bottom={4}
      pl={1}
      py={1}
      fontSize="sm"
      pointerEvents="none"
      zIndex={1}
      w={`${scale.px}px`}
      justify="center"
      borderBottomRadius="base"
      borderWidth={baseBorderWidth}
      borderColor="brand.analytic"
      borderTopColor="transparent"
      // Only animate the width when the meters change otherwise it jumps too much
      transition={animatingWidth ? "300ms right, 100ms width" : "300ms right"}
      onTransitionEnd={() => {
        setAnimatingWidth(false);
      }}
    >
      {scaleText}
      <Box
        zIndex={-1}
        position="absolute"
        w="full"
        h={`calc(100% - ${baseBorderWidth} * 2)`}
        top={`calc(${baseBorderWidth} * 2)`}
        left={0}
        bg="brand.grey.100"
        opacity={0.8}
        borderBottomRadius="base"
      />
    </Flex>
  );
};
